import type {
  InventoryVehicle,
  SchedulingAvailabilityInfo
} from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { type Result } from '@carvana/merch-result';
import deepmerge from 'deepmerge';
import { isPlainObject } from 'is-plain-object';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { Pagination } from '~/contracts/common';
import type { UserDeliveryInfo } from '~/contracts/srp';
import InventoryConditionalTile from '~/contracts/srp/InventoryConditionalTile';
import type { PricingResponse } from '~/services/common/contracts';
import type { DeepPartial } from '~/types';
import isServerSideRendering from '~/utilities/isServerSideRendering';

function getPageNumberFromUrl(): number {
  if (isServerSideRendering) return 1;

  const pageNumber = parseInt(
    new URL(window.location.href).searchParams.get('page') ?? '1',
    10
  );

  return Number.isNaN(pageNumber) || pageNumber <= 1 ? 1 : pageNumber;
}

export type Inventory = {
  inventory: {
    conditionalTiles?: InventoryConditionalTile[];
    pagination: Partial<Pagination>;
    vehicles: InventoryVehicle[];
  };
  searchRequestId: string;
  userDeliveryInfo?: UserDeliveryInfo;
};

export type SimilarVehicles = {
  vehicles: InventoryVehicle[];
  searchRequestId: string;
};

export type AvailabilityData = {
  soonestPickup: Result<SchedulingAvailabilityInfo>;
  soonestDelivery: Result<SchedulingAvailabilityInfo>;
};

export type ContextType = {
  fetchSearchTrigger: number;
  freeTextNoResults: boolean;
  inventory: LoadableState<Inventory>;
  inventoryBulkData: LoadableState<Record<number, AvailabilityData>>;
  previousSearchRequestId: string;
  searchAnalyzeTrigger: boolean;
  similarVehicles: SimilarVehicles;
  resetInventoryPricing: () => void;
  setPreviousSearchRequestId: React.Dispatch<React.SetStateAction<string>>;
  setSearchAnalyzeTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  setSimilarVehicles: React.Dispatch<React.SetStateAction<SimilarVehicles>>;
  triggerFetchSearch: () => void;
  updateInventoryPricing: (_data: PricingResponse) => void;
};

export const defaultInventory: Inventory = {
  inventory: {
    conditionalTiles: [],
    pagination: {
      currentPage: getPageNumberFromUrl()
    },
    vehicles: []
  },
  searchRequestId: '',
  userDeliveryInfo: undefined
};

const defaultContext: ContextType = {
  fetchSearchTrigger: 0,
  freeTextNoResults: false,
  inventory: defaultLoadableState<Inventory>(defaultInventory),
  inventoryBulkData: defaultLoadableState<Record<number, AvailabilityData>>({}),
  previousSearchRequestId: '',
  searchAnalyzeTrigger: false,
  similarVehicles: { searchRequestId: '', vehicles: [] },
  resetInventoryPricing() {},
  setPreviousSearchRequestId() {},
  setSearchAnalyzeTrigger() {},
  setSimilarVehicles() {},
  triggerFetchSearch() {},
  updateInventoryPricing() {}
};

const InventoryContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  freeTextNoResults?: boolean;
  inventoryData?: Inventory;
  similarVehiclesFromServer?: SimilarVehicles;
};

export function InventoryContextProvider({
  children,
  freeTextNoResults = defaultContext.freeTextNoResults,
  inventoryData,
  similarVehiclesFromServer
}: React.PropsWithChildren<OwnProps>) {
  const loadableInventoryState = useLoadableState<Inventory>(
    inventoryData ?? defaultInventory,
    {
      initializeAsLoaded: Boolean(
        inventoryData?.inventory?.vehicles?.length ||
          similarVehiclesFromServer?.vehicles?.length
      )
    }
  );
  const [fetchSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSearchTrigger
  );
  const [similarVehicles, setSimilarVehicles] = useState<SimilarVehicles>(
    similarVehiclesFromServer ?? defaultContext.similarVehicles
  );
  const inventoryBulkData = useLoadableState<Record<number, AvailabilityData>>(
    {}
  );

  const [previousSearchRequestId, setPreviousSearchRequestId] = useState('');
  const [searchAnalyzeTrigger, setSearchAnalyzeTrigger] = useState(false);

  const updateInventoryPricing = useCallback(
    (payload: PricingResponse) => {
      loadableInventoryState.setData.call(
        null,
        prevState => ({
          ...prevState,
          inventory: {
            ...prevState.inventory,
            vehicles: prevState.inventory.vehicles.map(vehicle => {
              const {
                pricingErrorMessage = '',
                termInMonths,
                ...pricingData
              } = payload.vehiclePaymentTermsMapping[vehicle.vehicleId] ?? {
                pricingErrorMessage: 'Terms not found from pricing'
              };

              return {
                ...vehicle,
                price: vehicle.price
                  ? {
                      ...vehicle.price,
                      tradeIncentive: undefined, // these ensure we don't carry over the old incentive when switching trades
                      tradeTaxSavings: undefined,
                      ...pricingData,
                      pricingErrorMessage,
                      term: termInMonths
                    }
                  : undefined
              };
            })
          }
        }),
        { shouldSetLoaded: false }
      );
    },
    [loadableInventoryState.setData]
  );

  const resetInventoryPricing = useCallback(() => {
    loadableInventoryState.setData.call(
      null,
      prevState => ({
        ...prevState,
        inventory: {
          ...prevState.inventory,
          vehicles: prevState.inventory.vehicles.map(vehicle => ({
            ...vehicle,
            price: vehicle.price
              ? {
                  ...vehicle.price,
                  downPayment: 0,
                  monthlyPayment: 0
                }
              : undefined
          }))
        }
      }),
      { shouldSetLoaded: false }
    );
  }, [loadableInventoryState.setData]);

  const triggerFetchSearch = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      fetchSearchTrigger,
      freeTextNoResults,
      inventory: loadableInventoryState,
      inventoryBulkData,
      previousSearchRequestId,
      searchAnalyzeTrigger,
      similarVehicles,
      resetInventoryPricing,
      setPreviousSearchRequestId,
      setSearchAnalyzeTrigger,
      setSimilarVehicles,
      triggerFetchSearch,
      updateInventoryPricing
    }),
    [
      fetchSearchTrigger,
      freeTextNoResults,
      inventoryBulkData,
      loadableInventoryState,
      previousSearchRequestId,
      searchAnalyzeTrigger,
      similarVehicles,
      resetInventoryPricing,
      triggerFetchSearch,
      updateInventoryPricing
    ]
  );

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

InventoryContextProvider.displayName = 'InventoryContextProvider';

export default InventoryContext;

export const generateInventoryContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState,
    { isMergeableObject: isPlainObject }
  );

export function MockedInventoryContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <InventoryContext.Provider value={generateInventoryContext(mockValue)}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useHasInventoryError(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.hasError;
}

export function useInventoryIsLoaded(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.isLoaded;
}

export function useFreeTextNoResults(): boolean {
  const { freeTextNoResults } = useContext(InventoryContext);

  return freeTextNoResults;
}

export function useInventoryVehicles(): InventoryVehicle[] {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.vehicles ?? [];
}

export function usePagination(): Partial<Pagination> {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination ?? {};
}

export function useSearchRequestId(): string {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.searchRequestId ?? '';
}

export function useSimilarVehicles() {
  const { similarVehicles } = useContext(InventoryContext);

  return similarVehicles;
}

export function useUserDeliveryInfo(): UserDeliveryInfo | undefined {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.userDeliveryInfo;
}

export function useRequestIds(): {
  searchRequestId: string;
  previousSearchRequestId: string;
} {
  const { inventory, previousSearchRequestId } = useContext(InventoryContext);

  return useMemo(
    () => ({
      searchRequestId: inventory.data.searchRequestId,
      previousSearchRequestId
    }),
    [inventory.data.searchRequestId, previousSearchRequestId]
  );
}

export function useInventoryBulkData(): Record<number, AvailabilityData> {
  const { inventoryBulkData } = useContext(InventoryContext);

  return inventoryBulkData.data ?? {};
}

export function useTotalMatchedInventory(): number {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination?.totalMatchedInventory ?? 0;
}
